import { useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertCircle, ArrowDown, Ban, CheckCircle, RefreshCcw, Sparkles } from 'lucide-react';
import { PaymentService } from '@/supabase/payment/paymentService';
import { toast } from 'sonner';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { PaymentRepository } from '@/supabase/payment/paymentRepository';
import { UpgradeDialog } from './UpgradeDialog';
import { logError } from '@/lib/sentry';

interface SubscriptionManageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  currentPlan: string;
  periodEnd: Date;
  hasDowngradePending: boolean;
  hasCancellationPending: boolean;
}

export function SubscriptionManageDialog({
  open,
  onOpenChange,
  userId,
  currentPlan,
  periodEnd,
  hasDowngradePending,
  hasCancellationPending
}: SubscriptionManageDialogProps) {
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showDowngradeConfirm, setShowDowngradeConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { refreshSubscription } = useSubscription();
  
  // 업그레이드 다이얼로그 상태
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [upgradeAmount, setUpgradeAmount] = useState(0);
  const [remainingDays, setRemainingDays] = useState(0);
  
  const paymentService = new PaymentService();
  
  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  
  // 다음 결제일 계산 함수 추가
  const getNextBillingDate = (date: Date) => {
    const nextDay = new Date(date);
    nextDay.setDate(nextDay.getDate() + 1);
    return formatDate(nextDay);
  };
  
  // 업그레이드 정보 계산 및 다이얼로그 표시
  const handleShowUpgradeDialog = useCallback(async () => {
    if (currentPlan !== 'standard') return;
    
    try {
      setIsLoading(true);
      
      // 1. 현재 구독 정보와 플랜 정보 조회
      const currentSubscription = await paymentService.getCurrentSubscription(userId);
      if (!currentSubscription) {
        toast.error('현재 구독 정보를 찾을 수 없습니다.');
        return;
      }

      // 플랜 정보 조회 (현재 플랜과 타겟 플랜)
      const standardPlan = await paymentService.getCurrentPlan(userId);
      const premiumPlan = await paymentService.getAllPlans().then(plans => 
        plans.find(plan => plan.id === 'premium')
      );
      
      if (!standardPlan || !premiumPlan) {
        toast.error('구독 플랜 정보를 찾을 수 없습니다.');
        return;
      }
      
      // 2. 남은 일수 계산
      const now = new Date();
      const endDate = new Date(currentSubscription.period_end);
      const days = Math.max(0, Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
      
      // 3. 업그레이드 금액 계산
      const priceDifference = premiumPlan.price - standardPlan.price;
      let amount = Math.round((priceDifference * days) / 30);
      amount = Math.max(100, amount);
      
      // 4. 상태 업데이트
      setUpgradeAmount(amount);
      setRemainingDays(days);
      setShowUpgradeDialog(true);
      
    } catch (error) {
      logError('업그레이드 견적 계산 중 오류:', error);
      toast.error('업그레이드 금액 계산 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  }, [userId, currentPlan, paymentService]);
  
  // 업그레이드 실행
  const executeUpgrade = async () => {
    if (!userId) return '';
    
    setIsLoading(true);
    try {
      // 업그레이드 로직 실행
      const paymentId = await paymentService.upgradeSubscription(userId, 'standard', 'premium');
      
      if (!paymentId) {
        console.log('업그레이드 결제가 취소되었습니다');
        return '';
      }
      
      console.log('업그레이드 성공:', paymentId);
      toast.success('프리미엄 플랜으로 성공적으로 업그레이드되었습니다');
      
      // 구독 정보 갱신
      await refreshSubscription();
      
      // 모든 다이얼로그 닫기
      setShowUpgradeDialog(false);
      onOpenChange(false);
      
      // 성공 후 페이지 새로고침
      setTimeout(() => window.location.reload(), 1000);
      
      return paymentId;
    } catch (error) {
      logError('업그레이드 실패:', error);
      
      // 에러 처리
      if (error instanceof Error) {
        toast.error(error.message || '업그레이드 처리 중 오류가 발생했습니다');
      } else {
        toast.error('업그레이드 처리 중 알 수 없는 오류가 발생했습니다');
      }
      
      return '';
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleCancelSubscription = async () => {
    try {
      setIsLoading(true);
      const subscription = await paymentService.getCurrentSubscription(userId);
      
      if (!subscription) {
        throw new Error('구독 정보를 찾을 수 없습니다.');
      }
      
      const repository = new PaymentRepository();
      await repository.updateSubscriptionStatus(
        subscription.id,
        'active', 
        true,
        null
      );
      
      await refreshSubscription();
      setShowCancelConfirm(false);
      onOpenChange(false);
      toast.success('구독 해지가 예약되었습니다. 다음 결제일부터 무료 플랜으로 전환됩니다.');
    } catch (error) {
      logError('구독 해지 중 오류 발생:', error);
      toast.error('구독 해지 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleDowngrade = async () => {
    try {
      setIsLoading(true);
      await paymentService.scheduleDowngrade(userId, 'standard');
      await refreshSubscription();
      setShowDowngradeConfirm(false);
      onOpenChange(false);
      toast.success('다운그레이드가 예약되었습니다. 다음 결제일부터 스탠다드 플랜으로 전환됩니다.');
    } catch (error) {
      logError('다운그레이드 예약 중 오류 발생:', error);
      toast.error('다운그레이드 예약 중 오류가 발생했습니다.' + error);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleCancelDowngrade = async () => {
    try {
      setIsLoading(true);
      await paymentService.cancelScheduledDowngrade(userId);
      await refreshSubscription();
      toast.success('다운그레이드 예약이 취소되었습니다.');
      onOpenChange(false);
    } catch (error) {
      logError('다운그레이드 예약 취소 중 오류 발생:', error);
      toast.error('다운그레이드 예약 취소 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleCancelSubscriptionCancel = async () => {
    try {
      setIsLoading(true);
      const subscription = await paymentService.getCurrentSubscription(userId);
      
      if (!subscription) {
        throw new Error('구독 정보를 찾을 수 없습니다.');
      }
      
      const repository = new PaymentRepository();
      await repository.updateSubscriptionStatus(
        subscription.id,
        'active', 
        false,
        subscription.downgrade_to
      );
      
      await refreshSubscription();
      toast.success('구독 해지 예약이 취소되었습니다.');
      onOpenChange(false);
    } catch (error) {
      logError('구독 해지 취소 중 오류 발생:', error);
      toast.error('구독 해지 취소 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // 업그레이드 다이얼로그
  if (showUpgradeDialog) {
    return (
      <UpgradeDialog
        open={open}
        onOpenChange={(val) => {
          if (!val) setShowUpgradeDialog(false);
          onOpenChange(val);
        }}
        onConfirm={executeUpgrade}
        remainingDays={remainingDays}
        amount={upgradeAmount}
        isLoading={isLoading}
      />
    );
  }
  
  if (showCancelConfirm) {
    return (
      <Dialog open={open} onOpenChange={(val) => {
        if (!val) setShowCancelConfirm(false);
        onOpenChange(val);
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>구독 해지 확인</DialogTitle>
            <DialogDescription>
              현재 구독을 해지하시겠습니까?
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
              <p className="text-sm text-amber-700 font-medium flex items-center">
                <AlertCircle className="h-4 w-4 mr-2" />
                구독 해지 시 환불되지 않습니다
              </p>
              <p className="text-sm text-amber-600 mt-1">
                구독을 해지하면 다음 결제일({getNextBillingDate(periodEnd)})부터 자동 결제가 중단되며, 무료 플랜으로 전환됩니다.
              </p>
              <p className="text-sm text-amber-600 mt-1">
                현재 결제 기간 동안에는 기존 서비스가 그대로 제공됩니다.
              </p>
            </div>
          </div>

          <DialogFooter className="flex flex-row justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setShowCancelConfirm(false)}
              disabled={isLoading}
            >
              취소
            </Button>
            <Button
              onClick={handleCancelSubscription}
              disabled={isLoading}
              variant="destructive"
            >
              {isLoading ? (
                <>
                  <RefreshCcw className="mr-2 h-4 w-4 animate-spin" />
                  처리 중...
                </>
              ) : '구독 해지하기'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
  
  if (showDowngradeConfirm) {
    return (
      <Dialog open={open} onOpenChange={(val) => {
        if (!val) setShowDowngradeConfirm(false);
        onOpenChange(val);
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>다운그레이드 확인</DialogTitle>
            <DialogDescription>
              프리미엄에서 스탠다드 플랜으로 다운그레이드하시겠습니까?
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-sm text-blue-700 font-medium flex items-center">
                <CheckCircle className="h-4 w-4 mr-2" />
                다음 결제일에 적용됩니다
              </p>
              <p className="text-sm text-blue-600 mt-1">
                다운그레이드는 다음 결제일({getNextBillingDate(periodEnd)})부터 적용되며, 해당 시점부터 스탠다드 플랜 요금이 청구됩니다.
              </p>
              <p className="text-sm text-blue-600 mt-1">
                현재 결제 기간 동안에는 프리미엄 기능을 계속 이용하실 수 있습니다.
              </p>
            </div>
          </div>

          <DialogFooter className="flex flex-row justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setShowDowngradeConfirm(false)}
              disabled={isLoading}
            >
              취소
            </Button>
            <Button
              onClick={handleDowngrade}
              disabled={isLoading}
              variant="default"
            >
              {isLoading ? (
                <>
                  <RefreshCcw className="mr-2 h-4 w-4 animate-spin" />
                  처리 중...
                </>
              ) : '다운그레이드 예약하기'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>구독 관리</DialogTitle>
          <DialogDescription>
            구독 상태를 관리할 수 있습니다.
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4 space-y-4">
          {hasCancellationPending ? (
            <div className="flex flex-col gap-3 p-4 bg-gray-50 border rounded-md">
              <div className="flex items-center text-amber-600">
                <AlertCircle className="h-5 w-5 mr-2 text-amber-600" />
                <span className="font-medium">구독 해지가 예약되어 있습니다</span>
              </div>
              <p className="text-sm text-gray-600">
                {formatDate(periodEnd)} 이후에 무료 플랜으로 전환됩니다.
              </p>
              <Button 
                variant="outline" 
                className="mt-2" 
                onClick={handleCancelSubscriptionCancel}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <RefreshCcw className="mr-2 h-4 w-4 animate-spin" />
                    처리 중...
                  </>
                ) : '구독 해지 취소하기'}
              </Button>
            </div>
          ) : hasDowngradePending ? (
            <div className="flex flex-col gap-3 p-4 bg-gray-50 border rounded-md">
              <div className="flex items-center text-blue-600">
                <ArrowDown className="h-5 w-5 mr-2 text-blue-600" />
                <span className="font-medium">다운그레이드가 예약되어 있습니다</span>
              </div>
              <p className="text-sm text-gray-600">
                {formatDate(periodEnd)} 이후에 스탠다드 플랜으로 전환됩니다.
              </p>
              <Button 
                variant="outline" 
                className="mt-2" 
                onClick={handleCancelDowngrade}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <RefreshCcw className="mr-2 h-4 w-4 animate-spin" />
                    처리 중...
                  </>
                ) : '다운그레이드 취소하기'}
              </Button>
            </div>
          ) : (
            <>
              {currentPlan === 'standard' && (
                <button
                  onClick={handleShowUpgradeDialog}
                  className="flex items-center justify-between w-full p-4 rounded-md border border-purple-200 hover:bg-purple-50 transition-colors bg-purple-50"
                  disabled={isLoading}
                >
                  <div className="flex items-center">
                    <Sparkles className="h-5 w-5 mr-3 text-purple-500" />
                    <div className="text-left">
                      <h3 className="font-medium text-gray-900">프리미엄으로 업그레이드</h3>
                      <p className="text-sm text-gray-500">더 많은 기능과 무제한 사용량을 이용하세요</p>
                    </div>
                  </div>
                  <span className="text-gray-400">&rarr;</span>
                </button>
              )}
              
              {currentPlan === 'premium' && (
                <button
                  onClick={() => setShowDowngradeConfirm(true)}
                  className="flex items-center justify-between w-full p-4 rounded-md border border-gray-200 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center">
                    <ArrowDown className="h-5 w-5 mr-3 text-blue-500" />
                    <div className="text-left">
                      <h3 className="font-medium text-gray-900">스탠다드로 다운그레이드</h3>
                      <p className="text-sm text-gray-500">다음 결제일부터 스탠다드 플랜으로 전환합니다</p>
                    </div>
                  </div>
                  <span className="text-gray-400">&rarr;</span>
                </button>
              )}
              
              {['standard', 'premium'].includes(currentPlan) && (
                <button
                  onClick={() => setShowCancelConfirm(true)}
                  className="flex items-center justify-between w-full p-4 rounded-md border border-gray-200 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center">
                    <Ban className="h-5 w-5 mr-3 text-red-500" />
                    <div className="text-left">
                      <h3 className="font-medium text-gray-900">구독 해지</h3>
                    </div>
                  </div>
                  <span className="text-gray-400">&rarr;</span>
                </button>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
} 